/**
 * AnnotateStage — the markup surface itself.
 *
 * The captured bitmap is painted onto a canvas and an SVG layer draws the
 * shapes on top in frame fractions, so the marks survive any stage size. The
 * whole thing is `absolute inset-0`: it is handed to `MediaTheater`'s overlay
 * slot and lies over the still-mounted `<video>`, which is never re-keyed or
 * unmounted while a frame is being marked up — the playhead, the decoder and
 * the controller binding all have to survive Discard.
 *
 * The tool strip is NOT here: it lives out of the picture, on the white
 * column below the theater — see `AnnotateBar`.
 */
import { useCallback, useEffect, useRef, useState, type Dispatch, type PointerEvent as ReactPointerEvent } from 'react';

import { NO_TIMEOUT, useTimeout } from '../../../../../lib/browser/useTimeout';
import { cn } from '../../../../../lib/utils';
import type { AnnotationAction, AnnotationState, DraftShape } from '../lib/annotationReducer';
import { toFraction } from '../lib/geometry';
import { capturePointer, releasePointer } from '../media/pointerCapture';
import { arrowHeadPoints, pointToPixels, shapeToPixels, strokeWidthFor } from './bakeFrame';

const WHITE_INK = '#FFFFFF';
const WHITE_SHADOW = 'drop-shadow(0 0 1.5px rgba(0,0,0,.8))';
/** The shutter flash: opaque for a beat, then a 340 ms fade back to the picture. */
const FLASH_HOLD_MS = 40;
const FLASH_FADE_MS = 340;

export const STAGE_LABEL = 'Captured frame — draw to mark it up';

export interface AnnotateStageProps {
  bitmap: ImageBitmap;
  /** Intrinsic size of the captured frame — the canvas' backing store. */
  width: number;
  height: number;
  state: AnnotationState;
  dispatch: Dispatch<AnnotationAction>;
  /** Baking: the frame is being flattened, so no new strokes may start. */
  busy?: boolean;
}

function shadowFor(colour: string): string | undefined {
  // White ink disappears on a bright frame without something behind it.
  if (colour.toUpperCase() !== WHITE_INK) return undefined;
  return WHITE_SHADOW;
}

interface ShapeSvgProps {
  shape: DraftShape;
  /** Frame size — the SVG's viewBox — so fractions map to the same pixels the bake uses. */
  width: number;
  height: number;
  stroke: number;
}

function pathFor(points: [number, number][], width: number, height: number): string {
  return points
    .map((point, index) => {
      const [x, y] = pointToPixels(point, width, height);
      const command = index === 0 ? 'M' : 'L';
      return `${command}${x} ${y}`;
    })
    .join(' ');
}

/**
 * The live overlay draws in frame pixels, exactly like `bakeFrame`, so what the
 * reviewer sees while marking up is what the note will store.
 */
function ShapeSvg({ shape, width, height, stroke }: ShapeSvgProps) {
  const style = { filter: shadowFor(shape.c) };

  switch (shape.k) {
    case 'pen': {
      // `rawPts` is the un-decimated path; `pts` is what a stored shape carries.
      const points = shape.rawPts ?? shape.pts ?? [];
      if (points.length < 2) return null;
      return <path d={pathFor(points, width, height)} fill="none" stroke={shape.c} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={style} />;
    }
    case 'arrow': {
      const [x1, y1] = pointToPixels([shape.x1 ?? 0, shape.y1 ?? 0], width, height);
      const [x2, y2] = pointToPixels([shape.x2 ?? 0, shape.y2 ?? 0], width, height);
      const [left, right] = arrowHeadPoints(x1, y1, x2, y2, stroke);
      const head = `${left[0]},${left[1]} ${x2},${y2} ${right[0]},${right[1]}`;
      return (
        <g stroke={shape.c} strokeWidth={stroke} fill="none" strokeLinecap="round" strokeLinejoin="round" style={style}>
          <line x1={x1} y1={y1} x2={x2} y2={y2} />
          <polyline points={head} fill="none" />
        </g>
      );
    }
    case 'box': {
      const box = shapeToPixels(shape, width, height);
      return <rect x={box.x} y={box.y} width={box.w} height={box.h} fill="none" stroke={shape.c} strokeWidth={stroke} style={style} />;
    }
    default: {
      // Compile-time exhaustiveness guard: a shape kind added to
      // AnchorShapeKind without a case here fails the build instead of
      // silently falling through to another kind's rendering.
      const exhaustiveCheck: never = shape.k;
      return exhaustiveCheck;
    }
  }
}

/**
 * The shutter flash. The stage mounts the moment a frame is grabbed, so the
 * flash plays on mount; `bitmap` is the trigger as well, which covers
 * capturing again straight from an open annotation.
 */
function CaptureFlash({ bitmap }: { bitmap: ImageBitmap }) {
  // The frame whose flash has faded; a new capture is lit until its hold runs out.
  const [fadedFor, setFadedFor] = useState<ImageBitmap | null>(null);
  const lit = fadedFor !== bitmap;
  const fade = useCallback(() => setFadedFor(bitmap), [bitmap]);
  useTimeout(fade, lit ? FLASH_HOLD_MS : NO_TIMEOUT, bitmap);

  return (
    <div
      aria-hidden="true"
      data-testid="capture-flash"
      style={{ transitionDuration: `${FLASH_FADE_MS}ms` }}
      className={cn('pointer-events-none absolute inset-0 bg-white transition-opacity ease-out motion-reduce:hidden', lit ? 'opacity-90' : 'opacity-0')}
    />
  );
}

export function AnnotateStage({ bitmap, width, height, state, dispatch, busy = false }: AnnotateStageProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  // Same weight the bake will use, so the live mark and the stored one match.
  const stroke = strokeWidthFor(width, height);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, width, height);
  }, [bitmap, width, height]);

  /**
   * The rect is read at event time, never cached: full view restyles this same
   * subtree into a fixed layer, so the surface can move and resize mid-stroke.
   */
  function fractionAt(e: ReactPointerEvent<HTMLDivElement>) {
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return toFraction(e.clientX, e.clientY, rect);
  }

  function handlePointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (busy) return;
    // Pointer capture keeps the stroke alive once it leaves the surface; the
    // reducer clamps the coordinates back into [0,1].
    capturePointer(e.currentTarget, e.pointerId);
    const { x, y } = fractionAt(e);
    dispatch({ type: 'start', x, y });
  }

  function handlePointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!state.draft) return;
    // The bake already read the shapes: a stroke that keeps growing through it
    // would be attached to a frame that was flattened without it. End it where
    // the bake started instead of extending it.
    if (busy) {
      dispatch({ type: 'end' });
      return;
    }
    const { x, y } = fractionAt(e);
    dispatch({ type: 'move', x, y });
  }

  function handlePointerEnd(e: ReactPointerEvent<HTMLDivElement>) {
    releasePointer(e.currentTarget, e.pointerId);
    if (!state.draft) return;
    dispatch({ type: 'end' });
  }

  return (
    <div
      ref={surfaceRef}
      role="img"
      aria-label={STAGE_LABEL}
      data-testid="annotate-stage"
      className="absolute inset-0 cursor-crosshair select-none"
      style={{ touchAction: 'none' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      <canvas ref={canvasRef} className="size-full" />
      <svg className="pointer-events-none absolute inset-0 size-full" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
        {state.shapes.map((shape, index) => (
          <ShapeSvg key={index} shape={shape} width={width} height={height} stroke={stroke} />
        ))}
        {state.draft ? <ShapeSvg shape={state.draft} width={width} height={height} stroke={stroke} /> : null}
      </svg>
      <CaptureFlash bitmap={bitmap} />
    </div>
  );
}
